import * as s from '~js/utils/selectors';
import * as a from '~js/utils/attributes';
import * as c from '~js/utils/classes';
import { ProductVideo } from '~js/components/product-video';

const Flickity = themeVendor.Flickity;

export class ProductsList {
  constructor(section) {
    this.container = section.container;
    this.sectionId = this.container.dataset.sectionId;
    this.tabButtons = this.container.querySelectorAll(s.selectors$6.tabButton);
    this.tabItems = this.container.querySelectorAll(s.selectors$6.tabItem);
    this.slidersImages = this.container.querySelectorAll(s.selectors$6.sliderImages);
    this.slidersContents = this.container.querySelectorAll(s.selectors$6.sliderContents);
    this.videos = [];
    this.flktyImages = [];
    this.flktyContent = [];
    this.sliderResizeEvent = () => this.resizeSlider();

    this.initButtons();
    this.initSliders();
    this.initProductVideos();
    this.initProductModel();
    this.initShopifyXrLaunch();
    this.listen();
  }

  listen() {
    if (this.slidersImages.length > 0 || this.slidersContents.length > 0) {
      document.addEventListener('theme:resize', this.sliderResizeEvent);
    }
  }

  resizeSlider() {
    if (this.flktyImages.length > 0) {
      requestAnimationFrame(() => {
        this.flktyImages.forEach((flktyImages) => flktyImages.resize());
      });
    }

    if (this.flktyContent.length > 0) {
      requestAnimationFrame(() => {
        this.flktyContent.forEach((flktyContent) => flktyContent.resize());
      });
    }
  }

  initButtons() {
    if (this.tabButtons.length) {
      this.tabButtons.forEach((tabButton) => {
        tabButton.addEventListener('click', (e) => {
          if (tabButton.classList.contains(c.classes$6.tabButtonActive)) {
            return;
          }

          const currentTabAnchor = tabButton.getAttribute(a.attributes$5.href);
          const currentTab = this.container.querySelector(currentTabAnchor);
          const currentMedia = currentTab.querySelector(s.selectors$6.productMediaWrapper);
          const mediaId = currentMedia ? currentMedia.dataset.mediaId : null;
          const currentCircleText = currentTab.querySelector(s.selectors$6.circleText);

          this.tabButtons.forEach((button) => {
            button.classList.remove(c.classes$6.tabButtonActive);
          });
          this.tabItems.forEach((item) => {
            const circleText = item.querySelector(s.selectors$6.circleText);
            item.classList.remove(c.classes$6.tabItemActive);
            circleText?.classList.add(c.classes$6.isDisabled);

            if (theme.settings.animations) {
              item.querySelectorAll(`.${c.classes$6.aosAnimate}`).forEach((element) => {
                element.classList.remove(c.classes$6.aosAnimate);
                setTimeout(() => {
                  element.classList.add(c.classes$6.aosAnimate);
                });
              });
            }
          });

          tabButton.classList.add(c.classes$6.tabButtonActive);
          currentTab.classList.add(c.classes$6.tabItemActive);

          document.dispatchEvent(new CustomEvent('theme:resize')); // Trigger theme:resize event to refresh the slider height

          if (currentCircleText) {
            currentCircleText.classList.remove(c.classes$6.isDisabled);
            document.dispatchEvent(new CustomEvent('theme:scroll')); // Trigger theme:scroll event to refresh the circle-text values
          }

          this.handleProductVideos(currentTab, mediaId);

          e.preventDefault();
        });
      });
    }
  }

  initSliders() {
    this.slidersImages.forEach((sliderImages, idx) => {
      const contentsElement = sliderImages.closest(s.selectors$6.tabItem).querySelector(s.selectors$6.sliderContents);

      const flktyImages = new Flickity(sliderImages, {
        fade: true,
        pageDots: false,
        prevNextButtons: true,
        wrapAround: true,
        adaptiveHeight: true,
        asNavFor: contentsElement,
        on: {
          change: (index) => {
            if (this.flktyContent.length > 0) {
              this.flktyContent[idx].select(index);
            }
          },
        },
      });

      flktyImages.on('settle', (index) => {
        const elements = sliderImages.querySelectorAll(s.selectors$6.productMediaWrapper);

        for (let i = 0; i < elements.length; i++) {
          if (i === index) {
            elements[i].querySelector(s.selectors$6.productSingleMediaImage).removeAttribute('tabindex');
          } else {
            elements[i].querySelector(s.selectors$6.productSingleMediaImage).setAttribute('tabindex', '-1');
          }
        }
      });

      this.flktyImages.push(flktyImages);
    });

    this.slidersContents.forEach((sliderContent) => {
      const flktyContent = new Flickity(sliderContent, {
        fade: true,
        pageDots: false,
        prevNextButtons: false,
        wrapAround: true,
        adaptiveHeight: true,
      });

      flktyContent.on('settle', (index) => {
        const elements = sliderContent.querySelectorAll(s.selectors$6.productContentWrapper);

        for (let i = 0; i < elements.length; i++) {
          if (i === index) {
            elements[i].querySelectorAll(s.selectors$6.productLink).forEach((element) => {
              element.removeAttribute('tabindex');
            });
          } else {
            elements[i].querySelectorAll(s.selectors$6.productLink).forEach((element) => {
              element.setAttribute('tabindex', '-1');
            });
          }
        }
      });

      this.flktyContent.push(flktyContent);
    });
  }

  initProductVideos() {
    this.tabItems.forEach((item) => {
      if (item.classList.contains(c.classes$6.tabItemActive)) {
        this.handleProductVideos(item);
      }
    });
  }

  loadVideos(container, mediaId = null) {
    const videoObserver = new IntersectionObserver(
      (entries, observer) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const productVideo = new ProductVideo(container);

            this.videos.push(productVideo);
            container.setAttribute(a.attributes$5.deferredMediaLoaded, '');
            this.playToggle(mediaId);

            observer.unobserve(entry.target);
          }
        });
      },
      {
        root: null,
        rootMargin: '300px',
        threshold: [0, 0.1, 0.25, 0.5, 0.75, 1],
      }
    );

    videoObserver.observe(container);
  }

  handleProductVideos(container, mediaId = null) {
    if (!container.hasAttribute(a.attributes$5.deferredMediaLoaded)) {
      this.loadVideos(container, mediaId);
      return;
    }

    this.playToggle(mediaId);
  }

  playToggle(mediaId) {
    this.videos.forEach((element) => {
      if (typeof element.pauseContainerMedia === 'function' && mediaId) {
        element.pauseContainerMedia(mediaId, this.container);
        this.switchMedia(mediaId);
      }

      if (!mediaId && Object.keys(element.players).length === 0) {
        this.pauseContainerMedia(this.container);
      }
    });
  }

  switchMedia(mediaId) {
    const selectedMedia = this.container.querySelector(
      `${s.selectors$6.productMediaWrapper}[${a.attributes$5.mediaId}="${mediaId}"]`
    );
    const isFocusEnabled = !document.body.classList.contains(c.classes$6.noOutline);

    if (isFocusEnabled) {
      selectedMedia.focus();
    }

    selectedMedia.classList.remove(c.classes$6.mediaHidden);
    selectedMedia.dispatchEvent(new CustomEvent('theme:media:visible'), { bubbles: true });
  }

  pauseContainerMedia(container) {
    const mediaItems = container.querySelectorAll(s.selectors$6.productMediaWrapper);

    if (mediaItems.length === 0) return;

    mediaItems.forEach((media) => {
      media.dispatchEvent(new CustomEvent('theme:media:hidden'), { bubbles: true });
      media.classList.add(c.classes$6.mediaHidden);
    });
  }

  initProductModel() {
    const modelItems = this.container.querySelectorAll(s.selectors$6.productModel);
    if (modelItems.length) {
      modelItems.forEach((element) => {
        theme.ProductModel.init(element, this.sectionId);
      });
    }
  }

  initShopifyXrLaunch() {
    document.addEventListener('shopify_xr_launch', () => {
      const currentMedia = this.container.querySelector(
        `${s.selectors$6.productModel}:not(.${c.classes$6.mediaHidden})`
      );
      currentMedia.dispatchEvent(new CustomEvent('xrLaunch'));
    });
  }

  onBlockSelect(evt) {
    // Show selected tab
    evt.target.dispatchEvent(new Event('click'));
  }

  onUnload() {
    if (this.slidersImages.length > 0 || this.slidersContents.length > 0) {
      document.removeEventListener('theme:resize', this.sliderResizeEvent);
    }
  }
}